import { ApplyForGovFunds } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { AffiliationTable } from '@ovComponents/4-module/workflowCompletion/subSteps/account/affiliationTable';
import { GoalLinkingReview } from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import {
  getSortedAffiliations,
  sortedAffiliationRelations,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/utils';
import { Box, Grid, IconButton, Typography } from 'hephaestus/web';
import { CloseRounded as CloseRoundedIcon } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { getAcccountDetailComponent } from '../account.reviewItem';

type RespFamilyReviewItemProps = {
  account: Account;
  onDeleteAccount: () => void;
};
export const RespFamilyReviewItem = ({
  account,
  onDeleteAccount,
}: RespFamilyReviewItemProps) => {
  const { t } = useTranslation([
    'affiliationTypes',
    'components',
    'workflowCompletions',
  ]);
  const [yes, no] = [
    t('components:booleanOptions.yes'),
    t('components:booleanOptions.no'),
  ];

  const hasSuccessor = Boolean(account.affiliations?.length);
  const sortedAffiliations = getSortedAffiliations(
    account.affiliations ?? [],
    'relation',
    sortedAffiliationRelations,
  );

  const accountDetailsConfig = [
    {
      field: t(
        'workflowCompletions:accountReviewPanel.basicCanadaEducationSavingsGrant',
      ),
      value: account.applyForGovFunds?.includes(ApplyForGovFunds.BasicCesg)
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.canadaLearningBond'),
      value: account.applyForGovFunds?.includes(ApplyForGovFunds.Clb)
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.additionalCESG'),
      value: account.applyForGovFunds?.includes(ApplyForGovFunds.AdditionalCesg)
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.affiliate'),
      value: hasSuccessor ? yes : no,
    },
  ];

  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Grid
          container
          sx={{
            gap: 3,
          }}
        >
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid>
              <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                {t('workflowCompletions:accountReviewPanel.respFamily')}
              </Typography>
            </Grid>
            {!account.id && (
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                }}
              >
                <IconButton
                  onClick={onDeleteAccount}
                  aria-label='close'
                  size='xsmall'
                >
                  <CloseRoundedIcon fontSize='inherit' />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid
            container
            sx={{
              gap: 1,
            }}
          >
            {accountDetailsConfig.map((config) => (
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                {getAcccountDetailComponent(config)}
              </Grid>
            ))}
          </Grid>
          <Grid size={12}>
            <AffiliationTable
              affiliations={sortedAffiliations}
              showType={true}
              showRelation={true}
            />
          </Grid>
          <Grid size={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <GoalLinkingReview account={account} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RespFamilyReviewItem;
